import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { LIST_TRAINING_SAMPLES, DELETE_TRAINING_SAMPLE } from '../graphql/operations';
import { TrainingSample, TrainingSampleFilterInput } from '../types';

export interface UseTrainingSamplesOptions {
  limit?: number;
  initialFilter?: TrainingSampleFilterInput;
}

export interface UseTrainingSamplesReturn {
  samples: TrainingSample[];
  loading: boolean;
  error: any;
  loadingMore: boolean;
  hasMore: boolean;
  filter: TrainingSampleFilterInput;
  dateFilter: string;
  sortDirection: 'ASC' | 'DESC';
  setFilter: (filter: TrainingSampleFilterInput) => void;
  setDateFilter: (dateFilter: string) => void;
  refetch: () => void;
  loadMoreSamples: () => Promise<void>;
  handleApplyFilter: () => void;
  handleClearFilters: () => void;
  handleDateFilterChange: (period: string) => void;
  handleSortDirectionChange: (direction: 'ASC' | 'DESC') => void;
}

export const useTrainingSamples = (options: UseTrainingSamplesOptions = {}): UseTrainingSamplesReturn => {
  const { limit = 50, initialFilter = {} } = options;
  const location = useLocation();
  const [filter, setFilter] = useState<TrainingSampleFilterInput>(initialFilter);
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  const { data, loading, error, refetch, fetchMore } = useQuery(LIST_TRAINING_SAMPLES, {
    variables: { limit, filter, sortDirection }
  });

  const samples = data?.listTrainingSamples?.items || [];
  const nextToken = data?.listTrainingSamples?.nextToken;

  useEffect(() => {
    if (location.state?.refresh) {
      refetch({ filter, sortDirection });
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refetch, filter, sortDirection]);

  const getDateFilterQuery = useCallback((period: string) => {
    const now = new Date();
    let startDate: Date;

    switch (period) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3months':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return {};
    }

    return {
      created_at: {
        gte: startDate.toISOString()
      }
    };
  }, []);

  const loadMoreSamples = useCallback(async () => {
    if (!nextToken || loadingMore) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          nextToken,
          limit,
          filter: { ...filter, ...getDateFilterQuery(dateFilter) },
          sortDirection
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;

          return {
            listTrainingSamples: {
              ...fetchMoreResult.listTrainingSamples,
              items: [
                ...prevResult.listTrainingSamples.items,
                ...fetchMoreResult.listTrainingSamples.items
              ]
            }
          };
        }
      });
    } catch (error) {
      console.error('Error loading more samples:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [nextToken, loadingMore, fetchMore, limit, filter, getDateFilterQuery, dateFilter, sortDirection]);

  const handleApplyFilter = useCallback(() => {
    refetch({ filter, sortDirection });
  }, [refetch, filter, sortDirection]);

  const handleClearFilters = useCallback(() => {
    setFilter({});
    setDateFilter('all');
    refetch({ filter: {}, sortDirection });
  }, [refetch, sortDirection]);

  const handleDateFilterChange = useCallback((period: string) => {
    setDateFilter(period);
    const dateQuery = getDateFilterQuery(period);
    const newFilter = { ...filter, ...dateQuery };
    setFilter(newFilter);
    refetch({ filter: newFilter, sortDirection });
  }, [filter, getDateFilterQuery, refetch, sortDirection]);

  const handleRefetch = useCallback(() => {
    refetch({ filter, sortDirection });
  }, [refetch, filter, sortDirection]);

  const handleSortDirectionChange = useCallback((direction: 'ASC' | 'DESC') => {
    setSortDirection(direction);
    refetch({ filter, sortDirection: direction });
  }, [refetch, filter]);

  return {
    samples,
    loading,
    error,
    loadingMore,
    hasMore: !!nextToken,
    filter,
    dateFilter,
    sortDirection,
    setFilter,
    setDateFilter,
    refetch: handleRefetch,
    loadMoreSamples,
    handleApplyFilter,
    handleClearFilters,
    handleDateFilterChange,
    handleSortDirectionChange,
  };
};